import { ServiceFeature } from '@portal/models/enumerators/employees';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { User } from '@portal/models/module';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';
import { AccountTaxIdentifierType, Permission } from '~/models/enumerators';

import {
  availableSoon,
  cards,
  cashback,
  condominiums,
  employees,
  escrow,
  investments,
  loan,
  multiUser,
  myAccount,
  payment,
  payslip,
  pix,
  tradingNotes,
  transfer,
} from '@portal/config/app.json';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import { selectRechargeEnabled } from '@portal/utils/accountSettings';
import { hasAccess } from '@portal/utils/hasAccess';
import { matchPath } from 'react-router-dom';

const routes: models.Route[] = [
  {
    id: 'HOME',
    name: 'Início',
    route: '/inicio',
    icon: '/assets/svg/panel-sidebar/ic_home.svg',
    iconAlt: 'Home',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [
      RolesType.TRANSACTION_READ,
      RolesType.CARD_READ,
      RolesType.CARD_MANAGE,
      RolesType.PIX_MANAGE_KEYS,
      RolesType.PIX_CREATE_TRANSACTION,
      RolesType.PIX_CONTROL_KEYS,
      RolesType.TRANSACTION_CREATE,
      RolesType.EMPLOYEES_PAYMENT_APPROVE,
      RolesType.EMPLOYEES_READ,
    ],
    items: [
      {
        id: 'HOME_PAGE',
        name: 'Transações',
        route: '',
        permission: Permission.MANAGE_USER,
        roles: [],
      },
      {
        id: 'MY_ACCOUNT_DETAILS',
        name: 'Extraction details',
        route: '/detalhes/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
      {
        id: 'TRANSFERS',
        name: 'Transfers',
        route: '/transferencia',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE, RolesType.TRANSACTION_READ],
      },
      {
        id: 'PAY',
        name: 'Pagar',
        route: '/pagar',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE],
      },
      {
        id: 'RECEBER',
        name: 'Receber',
        route: '/receber',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE],
      },
    ],
  },
  {
    id: 'APPROVED',
    name: 'Aprovação',
    route: '/aprovacao',
    icon: '/assets/svg/panel-sidebar/ic_approve.svg',
    iconAlt: 'aprovacoes',
    permission: [AccountTaxIdentifierType.CORPORATE],
    hidden: !transfer.twoFactorApprovals,
    roles: [RolesType.TRANSACTION_APPROVER, RolesType.TRANSACTION_READ],
    items: [
      {
        id: 'APPROVED_PAGE',
        name: 'Approved Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_APPROVER, RolesType.TRANSACTION_READ],
      },
      {
        id: 'APPROVED_DETAIL',
        name: 'Approved Page',
        route: '/detalhe-aprovacao/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_APPROVER, RolesType.TRANSACTION_READ],
      },
    ],
  },
  {
    id: 'PIX',
    name: 'Pix',
    route: '/pix',
    icon: '/assets/svg/panel-sidebar/ic_pix.svg',
    iconAlt: 'transferências',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [
      RolesType.PIX_MANAGE_KEYS,
      RolesType.TRANSACTION_CREATE,
      RolesType.PIX_CREATE_TRANSACTION,
      RolesType.PIX_CONTROL_KEYS,
    ],
    hidden: !pix.enabled && !availableSoon.pix,
    items: [
      {
        id: 'PIX',
        name: 'Pix',
        route: '/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'KEYS',
        name: 'Novas chaves',
        route: '/nova-chave',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.PIX_MANAGE_KEYS, RolesType.PIX_CONTROL_KEYS],
      },
      {
        id: 'COPY_PASTE',
        name: 'Pix Copia e Cola',
        route: '/pix-copia-cola',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE, RolesType.PIX_CREATE_TRANSACTION],
      },
      {
        id: 'WITHDRAW_AND_CHANGE',
        name: 'Pix Saque e Pix Troco',
        route: '/pix-saque-troco',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE, RolesType.PIX_CREATE_TRANSACTION],
      },
      {
        id: 'TRANSFER',
        name: 'Transferir',
        route: '/transferir',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE, RolesType.PIX_CREATE_TRANSACTION],
      },
      {
        id: 'LIMIT',
        name: 'Limite Pix',
        route: '/limite-pix',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'NOTIFICATIONS',
    name: 'Notificações',
    route: '/notificacoes',
    icon: '/assets/svg/panel-sidebar/ic_bell.svg',
    iconAlt: 'Notifications',
    hidden: true,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.TRANSACTION_READ],
    items: [
      {
        id: 'NOTIFICATIONS_PAGE',
        name: 'Notificações',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'LIMITS',
    name: 'Limites',
    route: '/limites',
    icon: '/assets/svg/panel-sidebar/ic_card.svg',
    iconAlt: 'Limits',
    hidden: !myAccount.accountLimits,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'LIMITS_PAGE',
        name: 'Meus limites',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
      {
        id: 'LIMITS_P2P',
        name: 'Limite P2P',
        route: '/limite-p2p',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
      {
        id: 'LIMITS_BILLET',
        name: 'Limite boleto',
        route: '/limite-boleto',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
      {
        id: 'LIMITS_TED',
        name: 'Limite TED',
        route: '/limite-ted',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
      {
        id: 'LIMITS_PIX',
        name: 'Limite Pix',
        route: '/limite-pix',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
      {
        id: 'LIMITS_API_GATEWAY_PIX',
        name: 'Limite Pix via API',
        route: '/limite-api-pix',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
    ],
  },
  {
    id: 'CARDS',
    name: 'Cartões',
    route: '/cartoes',
    icon: '/assets/svg/panel-sidebar/ic_card.svg',
    iconAlt: 'Card',
    hidden: !cards.enabled,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.CARD_MANAGE, RolesType.CARD_READ],
    items: [
      {
        id: 'REQUEST_CARD',
        name: 'REQUEST_CARD',
        route: '/solicitar-cartao',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.CARD_READ, RolesType.CARD_MANAGE],
      },
      {
        id: 'MY_CARDS_PAGE',
        name: 'Meus Cartões',
        route: '/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.CARD_READ, RolesType.CARD_MANAGE],
      },
      {
        id: 'MY_CARD_DETAILS',
        name: 'Detalhes do cartão',
        route: '/detalhes-cartao/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.CARD_READ, RolesType.CARD_MANAGE],
      },
      {
        id: 'MY_CARD_INVOICE',
        name: 'Fatura do cartão',
        route: '/fatura-cartao/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.CARD_READ, RolesType.CARD_MANAGE],
      },
      {
        id: 'TRACKING_CARD',
        name: 'Rastreamento do cartão',
        route: '/rastreio/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.CARD_READ, RolesType.CARD_MANAGE],
      },
      {
        id: 'ACCOUNT_ON_DOCK_PAGE',
        name: 'Criação de Conta',
        route: '/adesao-conta',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.CARD_READ, RolesType.CARD_MANAGE],
      },
    ],
  },
  {
    id: 'INVESTMENTS',
    name: 'Investimentos',
    route: '/investimentos',
    icon: '/assets/svg/panel-sidebar/ic_investments.svg',
    iconAlt: 'Investmentos',
    hidden: !investments.enabled,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'INVESTMENTS_PAGE',
        name: 'Investimentos',
        route: '/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'NEW_APPLICATION',
        name: 'Nova aplicação',
        route: '/nova-aplicacao/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE],
      },
      {
        id: 'INVESTMENT_RECEIPT',
        name: 'Comprovante',
        route: '/detalhes/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
    ],
  },
  {
    id: 'INVESTMENT_POSITION',
    name: 'Posição de investimento',
    route: '/posicao-de-investimento',
    icon: '/assets/svg/panel-sidebar/ic_investment_position.svg',
    iconAlt: 'Posição de investimento',
    hidden: !investments.investmentPosition,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'INVESTMENT_POSITION_PAGE',
        name: 'Posição de investimento',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'INVESTMENT_POSITION_PAGE_DETAILS',
        name: 'Detalhes de investimento',
        route: '/detalhes/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'TRADING_NOTES',
    name: 'Notas de negociação',
    route: '/notas-de-negociacoes',
    icon: '/assets/svg/panel-sidebar/ic_note.svg',
    iconAlt: 'Notas de negociação',
    hidden: !tradingNotes.enabled,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'TRADING_NOTES_PAGE',
        name: 'Notas de negociação',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'TRADING_NOTES_PROOF',
        name: 'Comprovante da nota de negociação',
        route: '/comprovante/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'INVESTMENT_PRODUCTS_AVAILABLE',
    name: 'Produtos disponíveis',
    route: '/produtos-disponiveis',
    icon: '/assets/svg/panel-sidebar/ic_products.svg',
    iconAlt: 'Produtos de investimentos disponíveis',
    hidden: !investments.enabled || !investments.availableProducts,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'INVESTMENT_PRODUCTS_AVAILABLE_PAGE',
        name: 'Produtos disponíveis',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'TRANSFERS',
    name: 'Transferir',
    route: '/transferencia',
    icon: '/assets/svg/panel-sidebar/ic_transfer.svg',
    iconAlt: 'transferências',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.TRANSACTION_CREATE],
    items: [
      {
        id: 'TRANSFERS',
        name: 'Transfers',
        route: '/',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_CREATE],
      },
    ],
  },
  {
    id: 'EXTRACT',
    name: 'Extrato',
    route: '/extrato',
    icon: '/assets/svg/panel-sidebar/ic_extract.svg',
    iconAlt: 'Account',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.TRANSACTION_READ],
    items: [
      {
        id: 'EXTRACT_PAGE',
        name: 'Extract Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
    ],
  },
  {
    id: 'RECEIPTS',
    name: 'Comprovantes',
    route: '/comprovantes',
    icon: '/assets/svg/panel-sidebar/ic_extract.svg',
    iconAlt: 'receipts',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    hidden: !transfer.batchReceipts,
    roles: [RolesType.TRANSACTION_READ],
    items: [
      {
        id: 'RECEIPTS_PAGE',
        name: 'Receipts Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
    ],
  },
  {
    id: 'PAYABLE',
    name: 'Contas a Pagar',
    route: '/contas-a-pagar',
    icon: '/assets/svg/panel-sidebar/ic_payment.svg',
    iconAlt: 'Payment',
    hidden: !payment.accountPayable,
    permission: payment.enablePersonPayable
      ? [AccountTaxIdentifierType.CORPORATE, AccountTaxIdentifierType.PERSON]
      : [AccountTaxIdentifierType.CORPORATE],
    roles: [
      RolesType.PAYABLE_READ,
      RolesType.PAYABLE_APPROVER,
      RolesType.PAYABLE_CREATE,
      RolesType.PAYABLE_DETAILS,
    ],
    items: [
      {
        id: 'ACCOUNT_PAYABLE',
        name: 'Contas a pagar',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [
          RolesType.PAYABLE_READ,
          RolesType.PAYABLE_APPROVER,
          RolesType.PAYABLE_CREATE,
          RolesType.PAYABLE_DETAILS,
        ],
      },
    ],
  },
  {
    id: 'FILES_HANDLER',
    name: 'Arquivos',
    route: '/arquivos',
    icon: '',
    iconAlt: '',
    hidden: true,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'FILE_DETAILS',
        name: 'Detalhes do arquivo',
        route: '/detalhes-arquivos/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'GENERATE_BY_FILE',
        name: 'Pagamentos por arquivos',
        route: '/gerar-arquivos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'RECEIVABLE',
    name: 'Contas a Receber',
    route: '/contas-a-receber',
    icon: '/assets/svg/panel-sidebar/ic_receivable.svg',
    iconAlt: 'Receivable',
    hidden: !payment.accountReceivable,
    permission: payment.enablePersonReceivable
      ? [AccountTaxIdentifierType.CORPORATE, AccountTaxIdentifierType.PERSON]
      : [AccountTaxIdentifierType.CORPORATE],
    roles: [
      RolesType.RECEIVABLE_READ,
      RolesType.RECEIVABLE_CREATE_BILLET,
      RolesType.RECEIVABLE_CREATE_LINK,
      RolesType.RECEIVABLE_CREATE_FILE,
    ],
    items: [
      {
        id: 'ACCOUNT_RECEIVABLE',
        name: 'Contas a receber',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [
          RolesType.RECEIVABLE_READ,
          RolesType.RECEIVABLE_CREATE_BILLET,
          RolesType.RECEIVABLE_CREATE_LINK,
          RolesType.RECEIVABLE_CREATE_FILE,
        ],
      },
    ],
  },
  {
    id: 'NEW_RECEIVABLE',
    name: 'Contas a Receber',
    route: '/receivable',
    icon: '/assets/svg/panel-sidebar/ic_receivable.svg',
    iconAlt: 'Receivable',
    hidden: !payment.newAccountReceivable,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.NEW_RECEIVABLE_READ],
    items: [
      {
        id: 'NEW_ACCOUNT_RECEIVABLE',
        name: 'Contas a receber',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'PAYSLIP',
    name: 'Holerite',
    route: '/holerite',
    icon: '/assets/svg/panel-sidebar/ic_holerite.svg',
    iconAlt: 'Payslip',
    hidden: !payslip.enabled,
    permission: [AccountTaxIdentifierType.CORPORATE],
    roles: [
      RolesType.PAYSLIP_READ,
      RolesType.PAYSLIP_CREATE,
      RolesType.PAYSLIP_DETAILS,
    ],
    items: [
      {
        id: 'PAYSLIP_PAYMENT',
        name: 'Holerite',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: false,
      },
      {
        id: 'PAYSLIP_DETAILS',
        name: 'Detalhes do holerite',
        route: '/detalhes-holerite/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'GENERATE_BY_FILE',
        name: 'Holerites',
        route: '/gerar-arquivo-holerite',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'ESCROW',
    name: 'Escrow',
    route: '/escrow',
    icon: '/assets/svg/panel-sidebar/ic_escrow.svg',
    iconAlt: 'Escrow',
    hidden: !escrow.enabled,
    permission: [AccountTaxIdentifierType.CORPORATE],
    roles: [RolesType.TRANSACTION_READ],
    items: [
      {
        id: 'ESCROW_PAGE',
        name: 'Escrow Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
      {
        id: 'ESCROW_PAGE_DETAILS',
        name: 'Escrow Page',
        route: '/detalhes/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
    ],
  },
  {
    id: 'CASHBACK',
    name: 'Cashback',
    route: '/cashback',
    icon: '/assets/svg/panel-sidebar/ic_cashback.svg',
    iconAlt: 'Cashback',
    hidden: !cashback.enabled && !cashback.money,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.TRANSACTION_READ],
    items: [
      {
        id: 'CASHBACK_PAGE',
        name: 'Cashback Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.TRANSACTION_READ],
      },
    ],
  },
  {
    id: 'LOAN',
    name: 'Empréstimo',
    route: '/emprestimo',
    icon: '/assets/svg/panel-sidebar/ic_loan.svg',
    iconAlt: 'Emprestimo',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    hidden: !loan.enabled,
    items: [
      {
        id: 'LOAN_PAGE',
        name: 'Loan Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'REGISTER',
        name: 'Cadastro',
        route: '/cadastro',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'SIMULATE',
        name: 'Simulação de empréstimo',
        route: '/simulacao',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'EMPLOYEES',
    name: 'Funcionários',
    route: '/funcionarios',
    icon: '/assets/svg/panel-sidebar/ic_employees.svg',
    iconAlt: 'Funcionários',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.EMPLOYEES_READ],
    hidden: !employees.enabled,
    items: [
      {
        id: 'EMPLOYEES_PAGE',
        name: 'Employees Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'EMPLOYEES_CSV',
        name: 'Employees CSV Page',
        route: '/upload-csv',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'EMPLOYEES_PAYMENT',
        name: 'Employees Payment Page',
        route: '/pagamentos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'EMPLOYEES_EXTRACT',
        name: 'Employees Extract Page',
        route: '/extrato-individual/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'PAYMENT_DETAILS',
        name: 'Detalhes do pagamento',
        route: '/detalhes-pagamentos/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'EASY_CREDIT',
        name: 'Crédito Fácil',
        route: '/credito-facil',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'APPROVE_PAYMENTS',
    name: 'Aprovar pagamentos',
    route: '/aprovar-pagamentos',
    icon: '/assets/svg/panel-sidebar/ic_approve.svg',
    iconAlt: 'aprovacoes',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.EMPLOYEES_PAYMENT_APPROVE],
    hidden: !employees.enabled,
    items: [
      {
        id: 'APPROVE_PAYMENTS_PAGE',
        name: 'Aprovar pagamentos',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'PROFILE',
    name: 'Configurações',
    route: '/minha-conta',
    icon: '/assets/svg/panel-sidebar/ic_user.svg',
    iconAlt: 'My Account',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [
      RolesType.ACCOUNT_CREATE,
      RolesType.ACCOUNT_READ,
      RolesType.ACCOUNT_UPDATE,
      RolesType.MANAGE_ACCOUNTS,
      RolesType.OFFLINE_ACCESS,
    ],
    items: [
      {
        id: 'MY_DEVICES',
        name: 'MY_DEVICES',
        route: '/dispositivos',
        permission: Permission.MANAGE_USER,
        roles: [RolesType.DEVICE_MANAGER],
        sidebarHidden: true,
      },
      {
        id: 'PROFILE_PAGE',
        name: 'PROFILE_Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'PROFILE_INFORMATION',
        name: 'Profile_Information',
        route: '/informacoes-pessoais',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'INCOME_TAX',
        name: 'Income_tax',
        route: '/imposto-de-renda',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'MULTI_ACCESS',
        name: 'Multi_access',
        route: '/multi-acessos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_ADD_USER, RolesType.MANAGE_ACCOUNTS],
      },
      {
        id: 'ACCOUNT_MULTI_ACCESS',
        name: 'Account_multi_access',
        route: '/conta-multi-acessos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_ADD_USER],
      },
      {
        id: 'CREDENTIAL_MULTI_ACCESS',
        name: 'Crendenciais_multi_access',
        route: '/crendenciais-multi-acessos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_ADD_USER],
      },
      {
        id: 'PERMISSIONS_MULTI_ACCESS',
        name: 'Permissions_multi_access',
        route: '/permissoes-multi-acessos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_ADD_USER],
      },
      {
        id: 'MY_PERMISSIONS_MULTI_ACCESS',
        name: 'My_permissions_multi_access',
        route: '/minhas-permissoes-multi-acessos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [],
      },
      {
        id: 'EDIT_MULTI_ACCESS_USER',
        name: 'Edit_multi_access_user',
        route: '/editar-usuarios-multi-acessos',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [],
      },
      {
        id: 'ACCOUNT_SELECTION',
        name: 'Account_selection',
        route: '/selecao-conta',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [],
      },
      {
        id: 'INVITE_USER',
        name: 'Invite_user',
        route: '/convidar-usuario',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_ADD_USER],
      },
      {
        id: 'EDIT_EXTERNAL_USER',
        name: 'Edit_external_user',
        route: '/editar-usuario-externo/:id',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_ADD_USER],
      },
      {
        id: 'ALTER_PASSWORD',
        name: 'Alter_Password',
        route: '/alterar-senha',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
        roles: [RolesType.ACCOUNT_UPDATE],
      },
      {
        id: 'PAY_STUB',
        name: 'Pay_Stub',
        route: '/holerite',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'SALARY_PORTABILITY',
        name: 'Salary_Portability',
        route: '/portabilidade-de-salario',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'CONFIGURATION',
        name: 'Configuration',
        route: '/configuracao',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'FAQ',
        name: 'Faq',
        route: '/perguntas-frequentes',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'LIMITS_TED',
        name: 'Limits_ted',
        route: '/limites-ted',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'LIMITS_BILLET',
        name: 'Limits_billet',
        route: '/limites-boleto',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'USER_MANAGER',
    name: 'Gestão de Usuários',
    route: '/gestao-usuarios',
    icon: '/assets/svg/panel-sidebar/ic_manager_user.svg',
    iconAlt: 'Home',
    permission: [],
    roles: [RolesType.MANAGE_GROUPS],
    hidden: !multiUser.enabled,
    items: [
      {
        id: 'USER_MANAGER',
        name: 'Menu',
        route: '',
        permission: Permission.MANAGE_GROUPS,
      },
      {
        id: 'USER_LIST',
        name: 'UserList',
        route: '/lista-operadores',
        permission: Permission.MANAGE_GROUPS,
        sidebarHidden: true,
      },
      {
        id: 'NEW_USER',
        name: 'NewUser',
        route: '/cadastrar-operador',
        permission: Permission.MANAGE_GROUPS,
        sidebarHidden: true,
      },
    ],
  },
  {
    hidden: true,
    id: 'PROFILE_MANAGER',
    name: 'ManagerGroup',
    route: '/gestao-perfis',
    icon: '/assets/svg/ic_manager_user.svg',
    iconAlt: 'Manager Group',
    permission: [AccountTaxIdentifierType.CORPORATE],
    roles: [RolesType.MANAGE_GROUPS],
    items: [
      {
        id: 'PROFILE_LIST',
        name: 'ProfileList',
        route: '/lista-grupos',
        permission: Permission.MANAGE_GROUPS,
        sidebarHidden: true,
        roles: [RolesType.MANAGE_GROUPS],
      },
      {
        id: 'NEW_PROFILE',
        name: 'NewUser',
        route: '/cadastrar-grupo',
        permission: Permission.MANAGE_GROUPS,
        sidebarHidden: true,
        roles: [RolesType.MANAGE_GROUPS],
      },
      {
        id: 'EDIT_PROFILE',
        name: 'EditProfile',
        route: '/editar-grupo',
        permission: Permission.MANAGE_GROUPS,
        sidebarHidden: true,
        roles: [RolesType.MANAGE_GROUPS],
      },
    ],
  },
  {
    id: 'CONDOMINIUMS',
    name: 'Condomínios',
    route: '/condominios',
    icon: '/assets/svg/panel-sidebar/ic_condominiums.svg',
    iconAlt: 'Condominiums',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    hidden: !condominiums.enabled,
    items: [
      {
        id: 'CONDOMINIUMS_PAGE',
        name: 'Condominiums Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'UPLOAD_CONDOMINIUMS',
        name: 'Upload Condominiums',
        route: '/upload',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'ACCOUNT_SPLIT',
    name: 'Conta Split',
    route: '/conta-split',
    icon: '/assets/svg/panel-sidebar/ic_new_transfer.svg',
    iconAlt: 'Account Split',
    permission: [AccountTaxIdentifierType.CORPORATE],
    hidden: true,
    items: [
      {
        id: 'ACCOUNT_SPLIT_PAGE',
        name: 'Account Split Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
  {
    id: 'PRODUCTS',
    name: 'Produtos',
    route: '',
    icon: '/assets/svg/panel-sidebar/ic_overdraft.svg',
    iconAlt: 'Overdraft',
    hidden: true,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    roles: [RolesType.ACCOUNT_READ],
    items: [
      {
        id: 'PRODUCT_ESCROW',
        name: 'Escrow Page',
        route: '/escrow',
        permission: Permission.MANAGE_USER,
      },
      {
        id: 'LIMITS_ESCROW_OPERATIONAL',
        name: 'Escrow Limits',
        route: '/limites-operacionais',
        permission: Permission.MANAGE_USER,
      },
    ],
  },
  // TODO: Liberar quando JD disponibilizar
  // {
  //   id: 'OVERDRAFT',
  //   name: 'Limite de Crédito',
  //   route: '/limite-conta',
  //   icon: '/assets/svg/panel-sidebar/ic_overdraft.svg',
  //   iconAlt: 'Overdraft',
  //   hidden: true,
  //   permission: [
  //     AccountTaxIdentifierType.CORPORATE,
  //     AccountTaxIdentifierType.PERSON,
  //   ],
  //   roles: [RolesType.ACCOUNT_READ],
  //   items: [
  //     {
  //       id: 'OVERDRAFT_PAGE',
  //       name: 'Overdraft Page',
  //       route: '',
  //       permission: Permission.MANAGE_USER,
  //     },
  //   ],
  // },
  {
    id: 'AUTH',
    name: 'Autenticação',
    route: '',
    hidden: true,
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    items: [
      {
        id: 'PENDING_DOCUMENTS',
        name: 'Documentos Pendentes',
        route: '/documentos-pendentes',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'CHANGE_PASSWORD',
        name: 'Trocar Senha',
        route: '/trocar-credenciais/senha',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'CHANGE_PIN',
        name: 'Trocar PIN',
        route: '/trocar-credenciais/pin',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'OPEN_ACCOUNT',
        name: 'Abertura de Conta',
        route: '/abertura-de-conta',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'CELCOIN_DOCUMENTS',
        name: 'Documentos Celcoin',
        route: '/documentos-celcoin',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'DENIED_ACCESS',
        name: 'Acesso Negado',
        route: '/acesso-negado',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'RECOVER_PASSWORD',
        name: 'Recuperar Senha',
        route: '/recuperar-senha',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'RECOVER_LOGIN',
        name: 'Recuperar Login',
        route: '/recuperar-login',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'REGISTER',
        name: 'Cadastrar',
        route: '/cadastrar',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'REGISTER_NUMBER',
        name: 'Cadastrar Número',
        route: '/cadastrar-numero',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'BC_PROTEGE_VALIDATION',
        name: 'BC Protege+ Validação',
        route: '/bc-protege/validacao',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'BC_PROTEGE_ERROR',
        name: 'BC Protege+ Erro',
        route: '/bc-protege/erro',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'BC_PROTEGE_INSTRUCTIONS',
        name: 'BC Protege+ Instruções',
        route: '/bc-protege/instrucoes',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
    icon: '',
    iconAlt: '',
  },
  {
    id: 'RECHARGE',
    name: 'Recargas',
    route: '/recargas',
    icon: '/assets/svg/panel-sidebar/ic_new_transfer.svg',
    iconAlt: 'Recharge Page',
    permission: [
      AccountTaxIdentifierType.CORPORATE,
      AccountTaxIdentifierType.PERSON,
    ],
    hidden: true,
    items: [
      {
        id: 'RECHARGE_SELECTION_PAGE',
        name: 'Recharge Selection Page',
        route: '',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
      {
        id: 'RECHARGE_HISTORY_PAGE',
        name: 'Recharge History Page',
        route: '/historico',
        permission: Permission.MANAGE_USER,
        sidebarHidden: true,
      },
    ],
  },
];

type ExtraRouteParams = {
  showInvestments?: boolean;
  cardEligibility?: boolean;
};

export const getRoutes = (
  permission: AccountTaxIdentifierType | null,
  loggedUser: User,
  extraParams: ExtraRouteParams = {
    showInvestments: false,
  }
): models.Route[] => {
  const { showInvestments, cardEligibility } = extraParams;
  const {
    user: { roles },
    account: { config },
  } = useReduxState();

  const isRechargeEnable = selectRechargeEnabled(config);

  if (
    permission &&
    (getPaymentAccountByType(loggedUser, PaymentAccountType.ACCOUNT) ||
      getPaymentAccountByType(loggedUser, PaymentAccountType.INVESTMENTS))
  ) {
    let allowedRoutes = routes.filter((route) => {
      const investmentsRoutes = [
        'INVESTMENTS',
        'INVESTMENT_POSITION',
        'TRADING_NOTES',
        'INVESTMENT_PRODUCTS_AVAILABLE',
      ];

      const isInvestmentRouteBlocked =
        investmentsRoutes.includes(route.id) && !showInvestments;
      const isCardsBlocked = route.id === 'CARDS' && !cardEligibility;
      const isRechargeBlocked = route.id === 'RECHARGE' && !isRechargeEnable;

      if (isInvestmentRouteBlocked || isCardsBlocked || isRechargeBlocked) {
        return false;
      }

      return route.permission?.includes(permission);
    });

    if (!roles?.includes(RolesType.EMPLOYEES_PAYMENT_APPROVE)) {
      allowedRoutes = allowedRoutes.filter(
        (route) => route.id !== 'APPROVE_PAYMENTS'
      );
    }

    if (
      loggedUser?.account?.financialFeature?.length === 0 ||
      !loggedUser?.account?.financialFeature?.some(
        (account) => account.serviceType === ServiceFeature.ALLOW_PAYMENT
      )
    ) {
      allowedRoutes = allowedRoutes.filter((route) => route.id !== 'EMPLOYEES');
    }

    if (!pix.enabled) {
      allowedRoutes = allowedRoutes.filter((route) => route.id !== 'PIX');
    }

    if (typeof loggedUser.canAccessPix !== 'undefined') {
      if (!loggedUser.canAccessPix) {
        allowedRoutes = allowedRoutes.filter((route) => route.id !== 'PIX');
      }
    }

    if (!getPaymentAccountByType(loggedUser, PaymentAccountType.ESCROW)) {
      allowedRoutes = allowedRoutes.filter(
        (route) => route.route !== getRouteStackPath('ESCROW', 'ESCROW_PAGE')
      );
    }

    return allowedRoutes;
  }

  if (getPaymentAccountByType(loggedUser, PaymentAccountType.ESCROW)) {
    return routes.filter((route) =>
      [
        getRouteStackPath('ESCROW', 'ESCROW_PAGE'),
        getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
        getRouteStackPath('ESCROW', 'ESCROW_PAGE_DETAILS'),
        getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS'),
      ].includes(route.route)
    );
  }

  if (getPaymentAccountByType(loggedUser, PaymentAccountType.LOAN)) {
    return routes.filter((route) =>
      [
        getRouteStackPath('LOAN', 'LOAN_PAGE'),
        getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
      ].includes(route.route)
    );
  }

  return [];
};

export const getRoutestack = (route: string): models.Route =>
  routes.find((o) => o.route === route) as models.Route;

export const getStackPath = (stackId: string): string => {
  return `${routes.find((o) => o.id === stackId)?.route}`;
};

export const routeExist = (route: string): boolean => {
  const routeTop = routes.find((o) => route.startsWith(o.route));

  if (!routeTop) {
    return false;
  }
  if (routeTop.route.length === route.length) {
    return false;
  }

  return (
    (routeTop.items.find((o) => `${routeTop.route}${o.route}` === route) &&
      true) ||
    false
  );
};

export const getRouteStackPath = (
  stackId: string,
  routeId: string,
  params?: Record<string, string | number>
): string => {
  const stack = routes.find((route) => route.id === stackId);
  const routeItem = stack?.items.find((item) => item.id === routeId);

  let path = `${stack?.route}${routeItem?.route}`;

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, String(value));
    });
  }

  return path;
};

export const isRouteAllowed = (
  routes: models.Route[],
  pathname: string,
  userAccountType: AccountTaxIdentifierType,
  userRoles: string[]
): boolean => {
  const normalizedPathname = pathname === '/' ? '/inicio' : pathname;

  let matchedRoute: models.Route | undefined;
  let matchedItem: models.routeInner | undefined;

  for (const route of routes) {
    if (route.route) {
      if (
        matchPath(normalizedPathname, {
          path: route.route,
          exact: false,
        })
      ) {
        matchedRoute = route;
      }
    }

    const item = route.items?.find((item) =>
      matchPath(normalizedPathname, {
        path: route.route ? `${route.route}${item.route}` : item.route,
        exact: false,
      })
    );

    if (item) {
      matchedRoute = route;
      matchedItem = item;
      break;
    }
  }

  if (!matchedRoute) {
    return false;
  }

  const permissions = matchedRoute.permission as
    | AccountTaxIdentifierType[]
    | undefined;

  if (permissions && !permissions.includes(userAccountType)) {
    return false;
  }

  const requiredRoles = matchedItem
    ? matchedItem.roles ?? matchedRoute.roles ?? []
    : [];

  if (requiredRoles.length > 0) {
    const canAccessByRole = hasAccess(userRoles, requiredRoles);
    if (!canAccessByRole) {
      return false;
    }
  }

  return true;
};
